import { subProjectService, type SubProject } from '../services/subProjectService';
import { projectService } from '../services/projectService';
import type { EntitySCV2Config } from './entitySCV2Config';

// Field permissions for SubProject entity
// Order here determines display order in the Metadata tab.
const SUBPROJECT_FIELDS_PERMISSIONS = {
  // Top 3 context boxes
  project_features: 'display' as const,
  problem_statement: 'editable' as const,
  state_of_the_art: 'display' as const,
  // T1 + T2 (solutions tables — rendered via customFieldRenderers)
  solutions: 'display' as const,
  // Other fields hidden from metadata tab (managed inside SolutionsBox / elsewhere)
  name: 'none' as const,
  features: 'none' as const,
  claims: 'none' as const,
  fto_analysis: 'none' as const,
  patentability: 'none' as const,
  patent_application: 'none' as const,
  industrialization: 'none' as const,
  features_list: 'none' as const,
  search_sessions_list: 'none' as const,
  kb_ids_list: 'none' as const,
  status: 'none' as const,
  context_kbs: 'none' as const,
  project_id: 'none' as const,
  kb_id: 'none' as const,
  kb_ids: 'none' as const,
  created_date: 'none' as const,
  last_updated: 'none' as const,
  id: 'none' as const,
};

// Field labels and descriptions
const SUBPROJECT_FIELD_LABELS = {
  name: {
    label: 'Sub-Project Name',
    description: 'The official name of the sub-project',
  },
  problem_statement: {
    label: 'Problem Statement',
    labelKey: 'scv2.fields.problem_statement',
    description: 'Description of the technical problem this sub-project addresses (max 5000 chars)',
  },
  project_features: {
    label: 'Product Features',
    labelKey: 'scv2.fields.project_features',
    description: 'Key technical features of the parent product (read-only, from Project)',
  },
  state_of_the_art: {
    label: 'State of the art',
    labelKey: 'scv2.fields.state_of_the_art',
    description: 'State of the art — AI-generated synthesis from Full KB Review',
  },
  features: {
    label: 'Features',
    labelKey: 'scv2.fields.features',
    description: 'Technical features and characteristics of the solution',
  },
  claims: {
    label: 'Claims',
    labelKey: 'scv2.fields.claims',
    description: 'Patent claims for the solution',
  },
  fto_analysis: {
    label: 'Freedom to Operate',
    labelKey: 'scv2.fields.fto_analysis',
    description: 'Freedom-to-operate analysis for the solution',
  },
  patentability: {
    label: 'Patentability',
    labelKey: 'scv2.fields.patentability',
    description: 'Patentability assessment for the solution',
  },
  patent_application: {
    label: 'Patent Application',
    labelKey: 'scv2.fields.patent_application',
    description: 'Patent application draft content for the solution',
  },
  industrialization: {
    label: 'Industrialization',
    labelKey: 'scv2.fields.industrialization',
    description: 'Industrialization and manufacturing assessment for the solution',
  },
  features_list: {
    label: 'Characteristics (product/software)',
    labelKey: 'scv2.fields.features_list',
    description: 'Technical features extracted from the problem statement for patent search',
  },
  search_sessions_list: {
    label: 'Searches',
    labelKey: 'scv2.fields.search_sessions_list',
    description: 'List of patent searches performed for this sub-project',
  },
  kb_ids_list: {
    label: 'Knowledge Bases',
    labelKey: 'scv2.fields.kb_ids_list',
    description: 'Knowledge bases linked to this sub-project',
  },
  status: {
    label: 'Status',
    description: 'Current status of the sub-project',
  },
  solutions: {
    label: 'Solutions',
    labelKey: 'scv2.fields.solutions',
    description: 'List of solutions defined for this sub-project',
  },
  project_id: {
    label: 'Parent Project',
    description: 'The project this sub-project belongs to',
  },
  created_date: {
    label: 'Created Date',
    description: 'When the sub-project was created',
  },
};

// Format date helper
const formatDate = (dateString: string, language: string): string => {
  return new Date(dateString).toLocaleDateString(
    language === 'fr' ? 'fr-FR' : 'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );
};

// SubProject SCV2 Configuration
export const subProjectSCV2Config: EntitySCV2Config<SubProject> = {
  type: 'sub-project',
  displayName: 'Sub-Projects',

  fieldsPermissions: SUBPROJECT_FIELDS_PERMISSIONS,
  fieldLabels: SUBPROJECT_FIELD_LABELS,

  service: {
    get: (id: string) => subProjectService.getById(id),
    update: (id: string, data: Partial<SubProject>) => subProjectService.update(id, data),
    delete: (id: string) => subProjectService.delete(id),
  },

  buildBreadcrumbs: async (subProject: SubProject) => {
    // Load parent project to build complete breadcrumb
    let parentProjectName = 'Project';
    try {
      const parentProject = await projectService.getProject(subProject.project_id);
      parentProjectName = parentProject.name;
    } catch (err) {
      console.warn('Failed to load parent project for breadcrumb:', err);
    }

    return [
      { path: `/scv2/project/${subProject.project_id}`, label: `Project: ${parentProjectName}` },
      { path: `/scv2/sub-project/${subProject.id}`, label: `Sub-Project: ${subProject.name}` },
    ];
  },

  buildActionButtons: (_subProject, handlers, permissions, t) => {
    return (
      <>
        {handlers.onNewSearch && (
          <button
            className="action-btn btn-search"
            onClick={handlers.onNewSearch}
          >
            {t('subProjectDetail.newSearch', 'New Search')}
          </button>
        )}
        {permissions.canDelete && (
          <button
            className="action-btn btn-delete"
            onClick={handlers.onDelete}
          >
            {t('subProjectDetail.deleteSubProject', 'Delete Sub-Project')}
          </button>
        )}
      </>
    );
  },

  getDetailPath: (subProject: SubProject) => `/scv2/sub-project/${subProject.id}`,
  
  getParentPath: (subProject: SubProject) => `/scv2/project/${subProject.project_id}`,

  formatDate,

  getKbIds: (subProject: SubProject) => subProject.kb_ids,

  getEntityName: (subProject: SubProject) => subProject.name,
};
